import {
  CardContent,
  Divider,
  Grid,
  List,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import NotificationsIcon from '@material-ui/icons/Notifications';
import Skeleton from '@material-ui/lab/Skeleton';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Card from '../../../../../components/Card';
import CardMessage from '../../../../../components/CardMessage/CardMessage';
import SelectPeriod from '../../../../../components/SelectPeriod';
import { NoticeDTO } from '../../../../../models/dto/NotificationDTO';
import { PeriodDTO } from '../../../../../models/dto/PeriodDTO';
import { AppState } from '../../../../../store';
import {
  getNotificationsByPeriod,
  setReadOrUnreadNotification,
} from '../../../../../store/actions/notificationsActions';
import { CondominiumReducerData } from '../../../../../store/reducers/condominiumReducer';
import NotificationItem from './NotificationItem';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
    },
    cardHeaderContainer: {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: 0,
    },
    notificationContentContainer: {
      height: 'calc(100% - 50px)',
      paddingTop: 0,
      overflowY: 'auto',
    },
    notificationTitleText: {
      fontWeight: 'bold',
      display: 'inline',
    },
    notificationText: {
      display: 'inline',
      marginLeft: theme.spacing(1),
    },
    newNotificationIcon: {
      color: theme.palette.info.main,
      fontSize: 12,
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    skeletonContainer: {
      paddingTop: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
  }),
);

const NotificationItemSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <Grid container spacing={0} className={classes.skeletonContainer}>
      <Grid item xs={12}>
        <Skeleton animation="wave" variant="text" width="100%" height={30} />
      </Grid>
      <Grid item xs={2}>
        <Skeleton animation="wave" width="100%" height={30} />
      </Grid>
    </Grid>
  );
};

const NotificationsCard: React.FC = () => {
  const classes = useStyles();
  const [periodSelected, setPeriodSelected] = useState<PeriodDTO | null>(null);
  const [notifications, setNotifications] = useState<NoticeDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const getNotifications = async () => {
    setLoading(true);
    if (condominiumSelected && periodSelected) {
      const data = await getNotificationsByPeriod(
        condominiumSelected.referencia,
        moment(periodSelected.inicioMesApuracao, 'DD/MM/YYYY').format(
          'YYYY-MM-DD HH:mm:ss',
        ),
        moment(periodSelected.fimMesApuracao, 'DD/MM/YYYY').format(
          'YYYY-MM-DD HH:mm:ss',
        ),
      );

      setNotifications(
        data.sort((a, b) =>
          moment(a.createdAt, 'DD/MM/YYYY HH:mm:ss').format(
            'YYYY-MM-DD HH:mm:ss',
          ) >=
          moment(b.createdAt, 'DD/MM/YYYY HH:mm:ss').format(
            'YYYY-MM-DD HH:mm:ss',
          )
            ? 1
            : -1,
        ),
      );
    }
    setLoading(false);
  };

  const changeNotificationVisualized = (item: NoticeDTO) => {
    const notificationsAux = [...notifications];
    const indexFound = notifications.findIndex(
      notification => notification.id === item.id,
    );

    notificationsAux[indexFound].visualizedAt = item.visualizedAt;

    setNotifications(notificationsAux);
    setReadOrUnreadNotification(item.id, item.visualizedAt);
  };

  useEffect(() => {
    getNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condominiumSelected, periodSelected]);

  return (
    <Card className={classes.card}>
      <Grid
        container
        alignItems="center"
        justify="space-between"
        className={classes.cardHeaderContainer}
      >
        <Grid item>
          <Typography color="textSecondary" variant="h5">
            Notificações
          </Typography>
        </Grid>
        <Grid item>
          <SelectPeriod
            showLabel
            disableUnderline
            value={periodSelected}
            handlePeriodChange={setPeriodSelected}
          />
        </Grid>
      </Grid>

      <CardContent className={classes.notificationContentContainer}>
        {loading ? (
          <>
            <NotificationItemSkeleton />
            <Divider />
            <NotificationItemSkeleton />
            <Divider />
            <NotificationItemSkeleton />
          </>
        ) : (
          <>
            {notifications.length > 0 ? (
              <List>
                {notifications.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    changeNotificationVisualized={changeNotificationVisualized}
                  />
                ))}
              </List>
            ) : (
              <CardMessage
                icon={
                  <NotificationsIcon style={{ fontSize: 130, opacity: 0.4 }} />
                }
                message="Nenhuma notificação"
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationsCard;
